// utils/messageDisplay.ts
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { BitMaskMessageContent } from "../generated/ln2/bit-mask-message-content";
import { OneHotMessageContent } from "../generated/ln2/one-hot-message-content";
import { LiftType } from "../../interfaces/Lift.interface";

import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";

import { ElevatorTraffic } from "../generated/traffic/elevator-traffic";
import { EscalatorTraffic } from "../generated/traffic/escalator-traffic";
import { ElevatorUsage } from "../generated/usage/elevator-usage";
import { EscalatorUsage } from "../generated/usage/escalator-usage";
import { ElevatorService } from "../generated/service/elevator-service";
import { EscalatorService } from "../generated/service/escalator-service";
import { ElevatorControl } from "../generated/control/elevator-control";
import { EscalatorControl } from "../generated/control/escalator-control";
import { ElevatorFault } from "../generated/fault/elevator-fault";
import { EscalatorFault } from "../generated/fault/escalator-fault";
import { ElevatorGroupSupervisoryStatus } from "../generated/group-supervisory-status/elevator-group-supervisory-status";
import { EscalatorGroupSupervisoryStatus } from "../generated/group-supervisory-status/escalator-group-supervisory-status";
import { ElevatorGroupControl } from "../generated/group-control/elevator-group-control";
import { EscalatorGroupControl } from "../generated/group-control/escalator-group-control";
import { ElevatorControllerInfoMessage } from "../generated/controller-info/elevator-controller-info-message";
import { EscalatorControllerInfoMessage } from "../generated/controller-info/escalator-controller-info-message";
import { Status } from "../generated/status/status";

function enumName<E extends Record<string, string | number>>(enm: E, value?: number | null): string {
	if (value == null) return "--";
	const name = (enm as any)[value] as string | undefined;
	return name ?? String(value);
}

function prettyFlatbuffersName(raw: string): string {
	const beforeUnderscore = raw.split("_")[0];
	return beforeUnderscore.replace(/Message$/, "");
}

export function contentTypeDisplayName(value?: EncodedMessageContent | null): string {
	if (value == null || value === EncodedMessageContent.NONE) return "--";
	return prettyFlatbuffersName(enumName(EncodedMessageContent, value));
}

export function messageTypeDisplayName(value?: BitMaskMessageContent | OneHotMessageContent | null): string {
	if (value == null || value === BitMaskMessageContent.NONE || value === OneHotMessageContent.NONE) return "--";
	const nameFromBitmask = (BitMaskMessageContent as any)[value] as string | undefined;
	if (nameFromBitmask) return prettyFlatbuffersName(nameFromBitmask);

	const nameFromOnehot = (OneHotMessageContent as any)[value] as string | undefined;
	if (nameFromOnehot) return prettyFlatbuffersName(nameFromOnehot);

	return String(value);
}

// ---------- helpers ----------
function enumLabel(enm: any, value?: number | null): string {
	if (value == null) return "--";
	return enm[value] ?? String(value);
}

function extractInnerMessageCode(content: any): number | null {
	if (!content) return null;

	if (typeof content.message === "number") return content.message;

	let cur: any = content;
	for (let i = 0; i < 5; i += 1) {
		if (!cur) return null;
		if (typeof cur.message === "number") return cur.message;
		cur = cur.message;
	}

	return null;
}

// ---------- main ----------
export function msgInnerDisplayName(args: {
	liftType: LiftType;
	contentType?: EncodedMessageContent | null;
	messageType?: OneHotMessageContent | BitMaskMessageContent | null;
	content?: BitMaskMessageT | OneHotMessageT | null;
}): string {
	const { liftType, contentType, messageType, content } = args;

	if (!contentType || !messageType || !content) return "--";

	// OneHot
	if (contentType === EncodedMessageContent.OneHotMessage) {
		const oneHot = content as OneHotMessageT;
		const code = extractInnerMessageCode(oneHot.message);
		if (code == null) return "--";

		if (messageType === OneHotMessageContent.Usage_UsageMessage) {
			return liftType === "ELEVATOR" ? enumLabel(ElevatorUsage, code) : enumLabel(EscalatorUsage, code);
		}
		if (messageType === OneHotMessageContent.Service_ServiceMessage) {
			return liftType === "ELEVATOR" ? enumLabel(ElevatorService, code) : enumLabel(EscalatorService, code);
		}

		return String(code);
	}

	// BitMask
	if (contentType === EncodedMessageContent.BitMaskMessage) {
		const bitmask = content as BitMaskMessageT;
		const code = extractInnerMessageCode(bitmask.message);
		if (code == null) return "--";

		if (messageType === BitMaskMessageContent.Traffic_TrafficMessage) {
			return liftType === "ELEVATOR" ? enumLabel(ElevatorTraffic, code) : enumLabel(EscalatorTraffic, code);
		}
		if (messageType === BitMaskMessageContent.Status_StatusMessage) {
			return enumLabel(Status, code);
		}
		if (messageType === BitMaskMessageContent.Control_ControlMessage) {
			return liftType === "ELEVATOR" ? enumLabel(ElevatorControl, code) : enumLabel(EscalatorControl, code);
		}
		if (messageType === BitMaskMessageContent.Fault_FaultMessage) {
			return liftType === "ELEVATOR" ? enumLabel(ElevatorFault, code) : enumLabel(EscalatorFault, code);
		}
		if (messageType === BitMaskMessageContent.GroupSupervisoryStatus_GroupSupervisoryStatusMessage) {
			return liftType === "ELEVATOR"
				? enumLabel(ElevatorGroupSupervisoryStatus, code)
				: enumLabel(EscalatorGroupSupervisoryStatus, code);
		}
		if (messageType === BitMaskMessageContent.GroupControl_GroupControlMessage) {
			return liftType === "ELEVATOR" ? enumLabel(ElevatorGroupControl, code) : enumLabel(EscalatorGroupControl, code);
		}
		if (messageType === BitMaskMessageContent.ControllerInfo_ControllerInfoMessage) {
			return liftType === "ELEVATOR"
				? enumLabel(ElevatorControllerInfoMessage, code)
				: enumLabel(EscalatorControllerInfoMessage, code);
		}

		return String(code);
	}

	return "--";
}
